import { v4 as uuidv4 } from 'uuid';
import { initializePdfJs, isPdfWorkerAvailable } from './pdfJsConfig';
import { extractPDFText, extractPDFTextAlternative, extractTextFromTextFile } from './fileProcessing/textExtractors';

// Helper function to get file type
export function getFileType(file: File): "pdf" | "png" | "jpg" | "jpeg" | "doc" | "txt" | null {
  const fileNameParts = file.name.split('.');
  const fileExtension = fileNameParts.pop()?.toLowerCase();

  switch (fileExtension) {
    case 'pdf':
      return "pdf";
    case 'png':
      return "png";
    case 'jpg':
      return "jpg";
    case 'jpeg':
      return "jpeg";
    case 'doc':
      return "doc";
    case 'txt':
      return "txt";
    default:
      return null;
  }
}

// Process file and extract text - for images, rely 100% on Google Vision API
export async function processFile(
  file: File,
  onProgress?: (progress: number) => void
): Promise<{ text: string, localUrl: string }> {
  const fileType = getFileType(file);
  if (!fileType) {
    throw new Error("Tipo de arquivo não suportado");
  }

  let extractedText = '';
  
  // Create a local URL for display
  const localUrl = URL.createObjectURL(file);
  
  try {
    if (fileType === 'pdf') {
      console.log(`Iniciando extração máxima de texto do PDF "${file.name}" (${Math.round(file.size/1024)} KB)`);
      
      // Start progress at 5%
      if (onProgress) onProgress(5);
      
      // Make sure PDF.js is initialized before processing
      if (!isPdfWorkerAvailable()) {
        if (onProgress) onProgress(10);
        const initialized = await initializePdfJs();
        if (!initialized) {
          console.warn("PDF.js não inicializado corretamente");
          await new Promise(resolve => setTimeout(resolve, 500));
          await initializePdfJs();
        }
      }
      
      console.log("PDF.js worker disponível:", isPdfWorkerAvailable());
      
      // Set progress to 15% after initialization
      if (onProgress) onProgress(15);
      
      // Try main extraction method first
      try {
        extractedText = await extractPDFText(file, onProgress);
        console.log(`Texto extraído: ${extractedText.length} caracteres`);
      } catch (mainError) {
        console.error("Falha no método principal de extração:", mainError);
        
        // If main method fails, try alternative methods
        try {
          console.log("Tentando método alternativo de extração...");
          extractedText = await extractPDFTextAlternative(file, onProgress);
          console.log(`Texto alternativo extraído: ${extractedText.length} caracteres`);
        } catch (altError) {
          console.error("Falha no método alternativo de extração:", altError);
          
          // As last resort, try to extract as binary content
          try {
            console.log("Tentando extração binária como último recurso...");
            const arrayBuffer = await file.arrayBuffer();
            const uint8Array = new Uint8Array(arrayBuffer);
            
            let binaryString = '';
            const chunkSize = 1024 * 1024; // Process 1MB at a time
            
            for (let i = 0; i < uint8Array.length; i += chunkSize) {
              const chunk = uint8Array.slice(i, i + chunkSize);
              for (let j = 0; j < chunk.length; j++) {
                // Only include ASCII printable characters
                if (chunk[j] >= 32 && chunk[j] <= 126) {
                  binaryString += String.fromCharCode(chunk[j]);
                }
              }
              
              // Update progress
              if (onProgress) {
                const progress = 50 + (i / uint8Array.length) * 40;
                onProgress(Math.min(90, progress));
              }
            }
            
            // Clean up the binary string
            let cleanedString = binaryString
              .replace(/\s+/g, ' ')
              .replace(/\.\s+/g, '.\n\n')
              .replace(/([a-zA-Z0-9])\s+(\d+)/g, '$1\n$2');
            
            extractedText = cleanedString;
            console.log(`Texto extraído do binário: ${extractedText.length} caracteres`);
          } catch (binaryError) {
            console.error("Falha na extração binária:", binaryError);
          }
        }
      }
      
      // If we got very little text, try alternative method
      if (extractedText.length < 1000) {
        console.log("Texto extraído insuficiente, tentando método alternativo...");
        try {
          const alternativeText = await extractPDFTextAlternative(file, onProgress);
          if (alternativeText && alternativeText.length > extractedText.length) {
            console.log(`Método alternativo extraiu mais texto: ${alternativeText.length} caracteres`);
            extractedText = alternativeText;
          }
        } catch (altError) {
          console.warn("Método alternativo falhou:", altError);
        }
      }
    } else if (fileType === 'png' || fileType === 'jpg' || fileType === 'jpeg') {
      // FOR IMAGES: Use 100% Google Vision API - NO local extraction
      console.log(`🖼️ Processing image "${file.name}" - EXCLUSIVE Google Vision API mode`);
      
      // Set initial progress
      if (onProgress) onProgress(20);
      
      // No local extraction for images - will be handled 100% by Google Vision in processor
      extractedText = `GOOGLE_VISION_PENDING:${file.name}`;
      
      // Simulate progress for UI feedback
      if (onProgress) {
        let progress = 20;
        const progressInterval = setInterval(() => {
          progress += 10;
          onProgress(Math.min(95, progress));
          if (progress >= 95) {
            clearInterval(progressInterval);
          }
        }, 150);
        
        // Complete progress after 1.5 seconds
        setTimeout(() => {
          clearInterval(progressInterval);
          if (onProgress) onProgress(100);
        }, 1500);
      }
    } else if (fileType === 'doc') {
      extractedText = `[Documento Word: ${file.name}]

📄 PROCESSAMENTO DE DOCUMENTO

Este documento Word será processado para extrair:
• Todo o texto formatado
• Tabelas e listas  
• Dados pessoais e jurídicos
• Estrutura do documento

Os dados extraídos serão analisados para oferecer sugestões inteligentes de uso.

Tamanho: ${(file.size / 1024).toFixed(1)} KB`;

      // Simulate progress for word files
      if (onProgress) {
        onProgress(25);
        setTimeout(() => onProgress(50), 300);
        setTimeout(() => onProgress(75), 600);
        setTimeout(() => onProgress(100), 900);
      }
    } else if (fileType === 'txt') {
      if (onProgress) onProgress(30);
      extractedText = await extractTextFromTextFile(file);
      if (onProgress) onProgress(100);
    }
    
    if (extractedText) {
      console.log(`Extração inicial bem-sucedida: ${extractedText.length} caracteres de ${file.name}`);
      // Set progress to 100% when complete
      if (onProgress) onProgress(100);
      return { text: extractedText, localUrl };
    } else {
      console.warn(`Texto extraído insuficiente para ${file.name}, adicionando informações padrão...`);
      
      // If still insufficient, add default text for analysis
      if (fileType === 'pdf') {
        extractedText = `[PDF: ${file.name}]

📄 DOCUMENTO PDF IDENTIFICADO

Não foi possível extrair o texto completo localmente.
      
Possíveis motivos:
• PDF protegido ou criptografado
• Imagens escaneadas sem texto selecionável
• Formato de PDF não compatível

Informações do arquivo:
• Nome: ${file.name}
• Tamanho: ${(file.size / 1024 / 1024).toFixed(2)} MB
• Tipo: ${file.type}

Para análise completa, recomendamos:
1. Verificar se o PDF contém texto selecionável
2. Usar ferramentas de OCR para extrair texto de imagens
3. Fornecer informações adicionais manualmente`;
      }
    }
    
    // Set progress to 100% when complete
    if (onProgress) onProgress(100);
    return { text: extractedText, localUrl };
  } catch (error) {
    console.error("Erro ao processar arquivo:", error);
    
    // Provide alternative text when extraction fails
    if (fileType === 'pdf') {
      extractedText = `[PDF: ${file.name}]

❌ ERRO NO PROCESSAMENTO

Não foi possível processar este PDF devido a:
${error}

Informações do arquivo:
• Nome: ${file.name}
• Tamanho: ${(file.size / 1024 / 1024).toFixed(2)} MB
• Tipo: ${file.type}

Soluções recomendadas:
1. Verificar se o arquivo não está corrompido
2. Tentar salvar o PDF em um formato diferente
3. Usar um conversor de PDF para texto
4. Fornecer as informações manualmente`;
    }
    
    // Set progress to 100% even on error
    if (onProgress) onProgress(100);
    
    return { 
      text: extractedText || `Ocorreu um erro ao processar o arquivo. Por favor, tente novamente ou use um formato diferente.\n\nDetalhes: ${error}`, 
      localUrl 
    };
  }
}
